import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, ShoppingBag, ImageOff } from "lucide-react";
import { useApp } from "@/lib/app-context";
import { inr, dt, errMsg } from "@/lib/api";
import { PageHeader, Panel, StatusBadge, Empty, NativeSelect, Field } from "@/components/common";
import { StockQuickEdit, SyncBadge } from "@/components/StockQuickEdit";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

function QuickPO({ row, onClose }) {
  const { api, branchId } = useApp();
  const [sups, setSups] = useState([]);
  const [sid, setSid] = useState(row.supplier_ids[0] || "");
  const [qty, setQty] = useState(row.suggested_reorder);
  useEffect(() => {
    api.get("/suppliers").then((r) => setSups(r.data));
  }, [api]);
  const save = async () => {
    try {
      const p = (await api.get(`/products/${row.product_id}`)).data;
      const r = await api.post("/purchase-orders", { supplier_id: sid, branch_id: branchId, items: [{ product_id: row.product_id, qty: Number(qty), cost: p.purchase_price || 0, tax_rate: p.tax_rate || 0 }] });
      toast.success(`${r.data.number} created`);
      onClose();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}><DialogContent data-testid="quick-po-dialog"><DialogHeader><DialogTitle>Reorder {row.name}</DialogTitle></DialogHeader>
      <Field label="Supplier"><NativeSelect testid="quick-po-supplier" value={sid} onChange={setSid} placeholder="Choose supplier" options={sups.map((s) => ({ value: s.id, label: s.name }))} /></Field>
      <Field label="Quantity"><Input data-testid="quick-po-qty" type="number" value={qty} onChange={(e) => setQty(e.target.value)} /></Field>
      <DialogFooter><Button onClick={save} disabled={!sid || !qty} data-testid="quick-po-create">Create purchase order</Button></DialogFooter></DialogContent></Dialog>
  );
}

export default function StockStatus({ kind }) {
  const { api, branchId, can } = useApp();
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [edit, setEdit] = useState(null);
  const [po, setPo] = useState(null);
  const load = () => api.get("/stock/status-list", { params: { kind, branch_id: branchId } }).then((r) => setRows(r.data));
  useEffect(() => {
    setRows(null);
    load();
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, [kind, branchId]); // eslint-disable-line react-hooks/exhaustive-deps
  const out = kind === "out";
  const shown = (rows || []).filter((r) => !q || `${r.name} ${r.sku} ${r.erp_code} ${r.category} ${r.subcategory}`.toLowerCase().includes(q.toLowerCase()));
  return (
    <div>
      <PageHeader title={out ? "Out of Stock" : "Low Stock"} subtitle={out ? "Products with zero sellable stock — shown as out of stock online and not purchasable. Restock to remove them from this list." : "Products still in stock but at or below their low-stock threshold."}>
        <span className="text-sm text-muted-foreground self-center" data-testid="status-count">{rows ? `${shown.length} products` : "Loading…"}</span>
      </PageHeader>
      <Panel>
        <Input placeholder="Search name, SKU, ERP ID, category" value={q} onChange={(e) => setQ(e.target.value)} className="mb-3 max-w-sm" data-testid="status-search-input" />
        {rows && !shown.length && <Empty text={out ? "Nothing is out of stock" : "No low-stock products"} />}
        {shown.length > 0 && <div className="overflow-x-auto"><table className="w-full text-sm" data-testid={`${kind}-stock-table`}>
          <thead className="text-left text-xs uppercase tracking-wider text-muted-foreground"><tr><th className="py-2 w-14"></th><th>Product</th><th>Category</th><th>Rack</th><th>Physical</th><th>Reserved</th><th>Available</th>
            {!out && <><th>Threshold</th><th>Reorder</th><th>Supplier</th></>}<th>Online ₹</th><th>Status</th>{out && <th>Last stock update</th>}<th>Website sync</th><th></th></tr></thead>
          <tbody>{shown.map((r) => (
            <tr key={r.product_id} className="border-t hover:bg-emerald-50/40" data-testid={`${kind}-row-${r.erp_code}`}>
              <td className="py-2">{r.image ? <img src={r.image} alt="" onError={(e) => { e.currentTarget.style.visibility = "hidden"; }} className="h-11 w-11 rounded-lg object-cover border bg-slate-100" /> : <div className="h-11 w-11 rounded-lg bg-slate-100 flex items-center justify-center"><ImageOff className="h-4 w-4 text-slate-400" /></div>}</td>
              <td><b>{r.name}</b><div className="font-mono text-[11px] text-muted-foreground">{r.erp_code}{r.sku && ` · ${r.sku}`}</div></td>
              <td className="text-xs">{r.category || "-"}{r.subcategory && <div className="text-muted-foreground">› {r.subcategory}</div>}</td>
              <td className="font-mono text-xs text-emerald-800">{r.racks.join(", ") || "-"}</td>
              <td>{r.physical}</td><td>{r.reserved}</td>
              <td className={`font-bold ${r.available <= 0 ? "text-red-600" : "text-amber-600"}`} data-testid={`available-${r.erp_code}`}>{r.available} {r.unit}</td>
              {!out && <><td>{r.threshold}</td><td>{r.suggested_reorder}</td><td className="text-xs">{r.suppliers.join(", ") || "-"}</td></>}
              <td>{inr(r.online_price)}</td><td><StatusBadge s={r.active ? "active" : "disabled"} /></td>
              {out && <td className="text-xs">{dt(r.last_stock_update)}</td>}
              <td><SyncBadge sync={r.sync} /></td>
              <td className="whitespace-nowrap"><Button size="sm" onClick={() => setEdit(r.product_id)} data-testid={`quick-edit-${r.erp_code}`}><Pencil className="h-3.5 w-3.5 mr-1" />{out ? "Restock / edit" : "Adjust"}</Button>
                {!out && can("purchase") && <Button size="sm" variant="ghost" onClick={() => setPo(r)} data-testid={`quick-po-${r.erp_code}`}><ShoppingBag className="h-3.5 w-3.5 mr-1" />PO</Button>}</td>
            </tr>))}</tbody></table></div>}
      </Panel>
      <StockQuickEdit productId={edit} onClose={() => setEdit(null)} onChanged={load} />
      {po && <QuickPO row={po} onClose={() => setPo(null)} />}
    </div>
  );
}
